import React from "react";
import type {
    ContextMeshCategoryKey,
    ContextMeshCategoryScores,
    ContextMeshTier
} from "./contextMeshAnalysis";
import {
    CONTEXT_MESH_CATEGORIES,
    CONTEXT_MESH_CATEGORY_THEME,
    CONTEXT_MESH_NAVY,
    type ContextMeshCategoryTheme
} from "./contextMeshTheme";

type ContextQuadrant = ContextMeshCategoryTheme & {
    bisector: number;
    top: boolean;
};

const QUADRANT_LAYOUT: Record<ContextMeshCategoryKey, Pick<ContextQuadrant, "bisector" | "top">> = {
    discover: { bisector: 225, top: true },
    external: { bisector: 315, top: true },
    code: { bisector: 45, top: false },
    file: { bisector: 135, top: false }
};

const QUADRANTS: ContextQuadrant[] = CONTEXT_MESH_CATEGORIES.map(category => ({
    ...category,
    ...QUADRANT_LAYOUT[category.key]
}));

const TIER_COLOR: Record<ContextMeshTier, string> = {
    GOOD: "#d97706",
    BETTER: "#2563eb",
    BEST: "#0f9f8f"
};
const BAND_COLOR = { STRONG: "#059669", GOOD: "#0d9488", THIN: "#d97706", WEAK: "#dc2626" } as const;

const MESH_NODES: [number, number][] = [
    [0, -60], [52, -34], [58, 20], [30, 58], [-30, 58], [-58, 20], [-52, -34], [0, 6], [26, 22], [-26, 22]
];
const MESH_COLORS = [
    CONTEXT_MESH_CATEGORY_THEME.discover.color,
    CONTEXT_MESH_CATEGORY_THEME.external.color,
    CONTEXT_MESH_CATEGORY_THEME.code.color,
    CONTEXT_MESH_CATEGORY_THEME.file.color,
    CONTEXT_MESH_CATEGORY_THEME.file.color,
    CONTEXT_MESH_CATEGORY_THEME.external.color,
    CONTEXT_MESH_CATEGORY_THEME.discover.color,
    CONTEXT_MESH_NAVY,
    CONTEXT_MESH_CATEGORY_THEME.code.color,
    CONTEXT_MESH_CATEGORY_THEME.file.color
];
const MESH_EDGES: [number, number][] = [
    [0, 1], [1, 2], [2, 3], [3, 4], [4, 5], [5, 6], [6, 0], [7, 0], [7, 2], [7, 4], [7, 6],
    [8, 2], [8, 3], [9, 4], [9, 5], [7, 8], [7, 9], [0, 2], [3, 5]
];

const radians = (degrees: number) => (degrees * Math.PI) / 180;
const point = (cx: number, cy: number, radius: number, angle: number): [number, number] => [
    cx + radius * Math.cos(radians(angle)),
    cy + radius * Math.sin(radians(angle))
];

function annular(cx: number, cy: number, innerRadius: number, outerRadius: number, start: number, end: number): string {
    const [x0Outer, y0Outer] = point(cx, cy, outerRadius, start);
    const [x1Outer, y1Outer] = point(cx, cy, outerRadius, end);
    const [x1Inner, y1Inner] = point(cx, cy, innerRadius, end);
    const [x0Inner, y0Inner] = point(cx, cy, innerRadius, start);
    return `M${x0Outer},${y0Outer} A${outerRadius},${outerRadius} 0 0 1 ${x1Outer},${y1Outer} L${x1Inner},${y1Inner} A${innerRadius},${innerRadius} 0 0 0 ${x0Inner},${y0Inner} Z`;
}

function textArc(cx: number, cy: number, radius: number, bisector: number, span: number, top: boolean): string {
    if (top) {
        const [x0, y0] = point(cx, cy, radius, bisector - span);
        const [x1, y1] = point(cx, cy, radius, bisector + span);
        return `M${x0},${y0} A${radius},${radius} 0 0 1 ${x1},${y1}`;
    }
    const [x0, y0] = point(cx, cy, radius, bisector + span);
    const [x1, y1] = point(cx, cy, radius, bisector - span);
    return `M${x0},${y0} A${radius},${radius} 0 0 0 ${x1},${y1}`;
}

function MeshGraph({ center, scale, yOffset }: { center: number; scale: number; yOffset: number }) {
    const nodes = MESH_NODES.map(([x, y]) => [center + x * scale, center + yOffset + y * scale]);
    return (
        <g>
            {MESH_EDGES.map(([a, b], index) => (
                <line key={index} x1={nodes[a][0]} y1={nodes[a][1]} x2={nodes[b][0]} y2={nodes[b][1]} stroke="#cbd5e1" strokeWidth={1.4 * scale} />
            ))}
            {nodes.map(([x, y], index) => (
                <circle key={index} cx={x} cy={y} r={(index === 7 ? 6.5 : 5) * scale} fill={MESH_COLORS[index]} />
            ))}
        </g>
    );
}

/**
 * QA-style Context Mesh wheel. The quadrants identify the four context inputs;
 * their colors are presentational only. The score arc and center value are the
 * aggregate values returned by the backend analyzer.
 */
export function StrengthWheel({
    score,
    tier,
    categoryScores = null,
    active = null,
    uid = "mesh",
    showHub = true,
    showLabels = true,
    showScoreGauge = true,
    discoverSubtitle,
    hubScoreFontSize = 42,
    hubGraphScale = 0.86,
    hubGraphYOffset = -30
}: {
    score: number;
    tier?: ContextMeshTier;
    categoryScores?: ContextMeshCategoryScores | null;
    active?: ContextMeshCategoryKey | null;
    uid?: string;
    showHub?: boolean;
    showLabels?: boolean;
    showScoreGauge?: boolean;
    discoverSubtitle?: string;
    hubScoreFontSize?: number;
    hubGraphScale?: number;
    hubGraphYOffset?: number;
}) {
    const size = 620;
    const center = size / 2;
    const outerRadius = 250;
    const innerRadius = 148;
    const scoreRadius = 268;
    const gaugeInnerRadius = outerRadius + 10;
    const gaugeOuterRadius = outerRadius + 24;
    const circumference = 2 * Math.PI * scoreRadius;
    const normalized = Math.max(0, Math.min(100, score));
    const scoreColor = tier ? TIER_COLOR[tier] : "#103699";

    return (
        <svg
            viewBox={`0 0 ${size} ${size}`}
            width="100%"
            height="100%"
            style={{ display: "block" }}
            data-testid="strength-wheel"
            role="img"
            aria-label={showScoreGauge ? (tier ? `Context Mesh score ${score}, tier ${tier}` : `Context Mesh preview score ${score}`) : "Context Mesh"}
        >
            {QUADRANTS.map(quadrant => {
                const start = quadrant.bisector - 45;
                const end = quadrant.bisector + 45;
                const titleId = `mesh-${uid}-${quadrant.key}-title`;
                const subtitleId = `mesh-${uid}-${quadrant.key}-subtitle`;
                const category = categoryScores?.[quadrant.key];
                const dimmed = active !== null && active !== quadrant.key;
                const opacity = dimmed ? 0.16 : 1;
                const ink = dimmed ? "#475569" : quadrant.labelColor;
                const filledEnd = category
                    ? start + 1.5 + (end - start - 3) * (category.score / 100)
                    : start + 1.5;
                const [scoreX, scoreY] = point(center, center, gaugeOuterRadius + 34, quadrant.bisector);
                return (
                    <g key={quadrant.key} data-testid={`strength-quadrant-${quadrant.key}`}>
                        <path d={annular(center, center, innerRadius, outerRadius, start, end)} fill={quadrant.color} opacity={opacity} />
                        {showLabels && (
                            <>
                                <defs>
                                    <path id={titleId} d={textArc(center, center, 208, quadrant.bisector, 34, quadrant.top)} />
                                    <path id={subtitleId} d={textArc(center, center, 178, quadrant.bisector, 30, quadrant.top)} />
                                </defs>
                                <text fontSize={21} fontWeight={700} fill={ink}>
                                    <textPath href={`#${titleId}`} startOffset="50%" textAnchor="middle">{quadrant.title}</textPath>
                                </text>
                                <text fontSize={12.5} fontWeight={600} fill={ink} fillOpacity={dimmed ? 0.9 : 0.86}>
                                    <textPath href={`#${subtitleId}`} startOffset="50%" textAnchor="middle">
                                        {quadrant.key === "discover" && discoverSubtitle ? discoverSubtitle : quadrant.subtitle}
                                    </textPath>
                                </text>
                            </>
                        )}
                        {category && (
                            <>
                                <path d={annular(center, center, gaugeInnerRadius, gaugeOuterRadius, start + 1.5, end - 1.5)} fill="#e2e8f0" />
                                {category.score > 2 && (
                                    <path
                                        d={annular(center, center, gaugeInnerRadius, gaugeOuterRadius, start + 1.5, filledEnd)}
                                        fill={quadrant.color}
                                        opacity={dimmed ? 0.35 : 1}
                                    />
                                )}
                                {showLabels && (
                                    <>
                                        <text x={scoreX} y={scoreY} textAnchor="middle" fontSize={27} fontWeight={700} fill={quadrant.accentColor} opacity={dimmed ? 0.35 : 1} fontFamily="var(--font-mono)">
                                            {category.score}
                                        </text>
                                        <text x={scoreX} y={scoreY + 15} textAnchor="middle" fontSize={11} fontWeight={700} letterSpacing="0.07em" fill={BAND_COLOR[category.band]} opacity={dimmed ? 0.35 : 1}>
                                            {category.band}
                                        </text>
                                    </>
                                )}
                            </>
                        )}
                    </g>
                );
            })}

            {!categoryScores && showScoreGauge && (
                <>
                    <circle cx={center} cy={center} r={scoreRadius} fill="none" stroke="#e2e8f0" strokeWidth={14} />
                    <circle
                        cx={center}
                        cy={center}
                        r={scoreRadius}
                        fill="none"
                        stroke={scoreColor}
                        strokeWidth={14}
                        strokeLinecap="round"
                        strokeDasharray={`${(normalized / 100) * circumference} ${circumference}`}
                        transform={`rotate(-90 ${center} ${center})`}
                    />
                </>
            )}
            <circle cx={center} cy={center} r={innerRadius - 6} fill="#fff" stroke="#e2e8f0" strokeWidth={1.5} />
            <MeshGraph center={center} scale={showHub ? hubGraphScale : 1.05} yOffset={showHub ? hubGraphYOffset : 0} />
            {showHub && (
                <>
                    <text data-testid="mesh-strength-score" x={center} y={center + 62} textAnchor="middle" fontSize={hubScoreFontSize} fontWeight={700} fill="#0f172a" fontFamily="var(--font-mono)">{score}</text>
                    <text x={center} y={center + 84} textAnchor="middle" fontSize={11} fontWeight={700} letterSpacing="0.1em" fill="#64748b">MESH STRENGTH</text>
                </>
            )}
        </svg>
    );
}

export function ContextMeshPlaceholder({ showLabel = true }: { showLabel?: boolean }) {
    const size = 620;
    const center = size / 2;
    return (
        <svg
            viewBox={`0 0 ${size} ${size}`}
            width="100%"
            height="100%"
            style={{ display: "block" }}
            data-testid="context-mesh-placeholder"
            role="img"
            aria-label="Context Mesh setup required"
        >
            <circle cx={center} cy={center} r={244} fill="#fff" stroke="#e2e8f0" strokeWidth={28} strokeDasharray="22 16" />
            <circle cx={center} cy={center} r={196} fill="#fff" stroke="#eef2f7" strokeWidth={2} />
            <MeshGraph center={center} scale={showLabel ? 0.86 : 1.05} yOffset={showLabel ? -48 : 0} />
            {showLabel && (
                <>
                    <text x={center} y={center + 75} textAnchor="middle" fontSize={21} fontWeight={750} letterSpacing="0.08em" fill="#64748b">SETUP</text>
                    <text x={center} y={center + 107} textAnchor="middle" fontSize={12} fontWeight={700} letterSpacing="0.09em" fill="#64748b">CONTEXT MESH</text>
                </>
            )}
        </svg>
    );
}
